/**
 * <brand-carousel> — a scroll-snap track with pagination dots.
 *
 * Deliberately not the PLP one: PlpContentRows binds its dots to the hardcoded
 * .plp-rows__row class, so reusing it would drag that section's styling in too.
 * This only needs the three data attributes below and styles nothing itself.
 *
 * Markup:
 *   <brand-carousel>
 *     <div data-brand-track>
 *       <div data-brand-slide>…</div>
 *     </div>
 *     <div data-brand-dots><button data-brand-dot></button></div>
 *     <div data-brand-progress><span data-brand-progress-fill></span></div>
 *   </brand-carousel>
 *
 * Dots and progress bar are both optional; either may be left out.
 *
 * The track is only scrollable at the widths the section makes it so; where it
 * is not, nothing overflows, the observer reports the first slide and the dots
 * are hidden by CSS. No breakpoint is hardcoded here.
 */
class BrandCarousel extends HTMLElement {
  connectedCallback() {
    if (this.initialised) return;
    this.initialised = true;

    this.track = this.querySelector('[data-brand-track]');
    this.slides = Array.from(this.querySelectorAll('[data-brand-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-brand-dot]'));
    this.progress = this.querySelector('[data-brand-progress-fill]');
    if (!this.track || this.slides.length < 2) return;

    if (this.progress) {
      this.rail = this.querySelector('[data-brand-progress]');
      this.updateProgress = this.updateProgress.bind(this);
      this.track.addEventListener('scroll', this.updateProgress, { passive: true });
      window.addEventListener('resize', this.updateProgress);
      if (this.rail) this.initRail();
      this.updateProgress();
    }

    if (this.dots.length < 2) return;

    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        const slide = this.slides[i];
        if (!slide) return;
        this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
      });
    });

    if ('IntersectionObserver' in window) {
      // 0.6 rather than a scroll listener: it reports the slide that actually
      // occupies the track, so a half-swipe never moves the dot early.
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.setCurrent(this.slides.indexOf(entry.target));
        });
      }, { root: this.track, threshold: 0.6 });

      this.slides.forEach((slide) => this.observer.observe(slide));
    }

    this.setCurrent(0);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this.progress) {
      this.track.removeEventListener('scroll', this.updateProgress);
      window.removeEventListener('resize', this.updateProgress);
    }
  }

  /**
   * The rail is the only control the design gives this slider, and the track
   * hides its scrollbar — so without this a mouse user could not move it at
   * all. Dragging or clicking the rail scrolls; arrow keys step a slide.
   */
  initRail() {
    const scrollToPointer = (event) => {
      const rect = this.rail.getBoundingClientRect();
      if (!rect.width) return;
      const { scrollWidth, clientWidth } = this.track;
      const scrollable = scrollWidth - clientWidth;
      if (scrollable <= 0) return;

      // The pointer marks the middle of the thumb, not its left edge, so the
      // bar lands under the cursor instead of jumping half its width past it.
      const share = clientWidth / scrollWidth;
      const ratio = (event.clientX - rect.left) / rect.width;
      const travelled = (ratio - share / 2) / Math.max(1 - share, 0.0001);
      this.track.scrollLeft = Math.min(Math.max(travelled, 0), 1) * scrollable;
    };

    this.rail.addEventListener('pointerdown', (event) => {
      this.rail.setPointerCapture(event.pointerId);
      this.dragging = true;
      scrollToPointer(event);
    });

    this.rail.addEventListener('pointermove', (event) => {
      if (this.dragging) scrollToPointer(event);
    });

    const endDrag = () => { this.dragging = false; };
    this.rail.addEventListener('pointerup', endDrag);
    this.rail.addEventListener('pointercancel', endDrag);

    this.rail.addEventListener('keydown', (event) => {
      const delta = { ArrowLeft: -1, ArrowRight: 1 }[event.key];
      if (!delta) return;
      event.preventDefault();
      const step = this.slides.length > 1
        ? this.slides[1].getBoundingClientRect().left - this.slides[0].getBoundingClientRect().left
        : this.track.clientWidth;
      this.track.scrollBy({ left: step * delta, behavior: 'smooth' });
    });
  }

  /**
   * The bar shows how much of the track is on screen and where: its width is
   * the visible share, its offset the distance travelled. A track that does not
   * overflow reports a full bar rather than a stuck sliver.
   */
  updateProgress() {
    const { scrollWidth, clientWidth, scrollLeft } = this.track;
    const scrollable = scrollWidth - clientWidth;
    const share = scrollWidth > 0 ? Math.min(clientWidth / scrollWidth, 1) : 1;
    const travelled = scrollable > 0 ? scrollLeft / scrollable : 0;

    this.progress.style.width = `${share * 100}%`;
    this.progress.style.transform = `translateX(${(travelled * (1 - share) * 100) / share}%)`;
  }

  setCurrent(index) {
    if (index < 0) return;
    this.dots.forEach((dot, i) => dot.setAttribute('aria-current', i === index ? 'true' : 'false'));
  }
}

customElements.define('brand-carousel', BrandCarousel);
